import React from 'react';
import { Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import AnnouncementBanner from './AnnouncementBanner';
import Header from './Header';
import CtaLadder from './CtaLadder';
import Footer, { SOCIAL_LINKS } from './Footer';
import Analytics from '../common/Analytics';
import ScrollToTop from '../common/ScrollToTop';
import JsonLd from '../common/JsonLd';
import { SITE, SITE_NAME } from '../common/PageMeta';
import { GITHUB_URL } from '../../common/constants';

// docs/SEO-AUDIT.md §6.1 — site-wide Organization + WebSite, present on
// every page (unlike BlogPosting/FAQPage/BreadcrumbList, which are
// per-route and live with the pages they describe). sameAs reuses
// Footer's actual social links instead of a second hardcoded copy.
const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [{
    '@type': 'Organization',
    '@id': `${SITE}/#org`,
    name: SITE_NAME,
    url: SITE,
    logo: `${SITE}/webclip.png`,
    email: 'info@openconceptlab.org',
    sameAs: [...SOCIAL_LINKS.map(l => l.href), GITHUB_URL],
  }, {
    '@type': 'WebSite',
    '@id': `${SITE}/#site`,
    url: SITE,
    name: SITE_NAME,
    publisher: { '@id': `${SITE}/#org` },
  }],
};

const Layout = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Helmet>
        {/* scripts/build-rss.js emits public/feed/index.xml, served at
            /feed/ — autodiscovery link for feed readers/browsers. */}
        <link rel="alternate" type="application/rss+xml" title={`${SITE_NAME} Blog`} href={`${SITE}/feed/`} />
      </Helmet>
      <JsonLd data={organizationJsonLd} />
      <Analytics />
      <ScrollToTop />
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute', left: -9999, top: 0, zIndex: 2000,
          p: 2, backgroundColor: 'background.paper', color: 'primary.main',
          '&:focus': { left: 8, top: 8 },
        }}>
        {t('common.skip_to_content')}
      </Box>
      <AnnouncementBanner />
      <Header />
      <Box component="main" id="main-content" tabIndex={-1} sx={{ flex: 1 }}>
        <Outlet />
      </Box>
      <CtaLadder />
      <Footer />
    </Box>
  );
};

export default Layout;
